import { Fragment, useEffect } from 'react'
import {
  Box,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useQuery, useQueryClient } from 'react-query'

// local imports
import default_company_photo from 'assets/images/default_company_photo.png'
import { getCompanyDetails } from 'services/api/company-details'
import { getOneJobAds } from 'services/api/modules/recruitment/job-advertisment'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const ViewJobAdsDialog = ({ open, handleClose, id, test = true }) => {
  const { data: companyDetails } = useQuery('companyDetails', getCompanyDetails)

  // Call API
  const queryClient = useQueryClient()
  useEffect(() => {
    queryClient.invalidateQueries('getOneJobAds')
    queryClient.resetQueries('getOneJobAds')
  }, [])

  const {
    isLoading: jobAdsIsLoading,
    error: jobAdsError,
    data: jobAdsData,
  } = useQuery(
    ['getOneJobAds', id],
    async () => {
      const { data } = await getOneJobAds(id)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const date = new Date(jobAdsData?.expired_date)
  const formattedDate = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  // job details data for component map
  const JOB_DETAILS = [
    {
      id: 1,
      title: 'Position Level',
      description: jobAdsData?.job_level_name,
    },
    {
      id: 2,
      title: 'Job Specialization',
      description: jobAdsData?.job_background_name,
    },
    { id: 3, title: 'Employment Type', description: jobAdsData?.position_type },
    {
      id: 4,
      title: 'Salary',
      description: `RM ${jobAdsData?.min_salary} - RM ${jobAdsData?.max_salary}`,
    },
    {
      id: 5,
      title: 'Work Location',
      description: `${jobAdsData?.city}, ${jobAdsData?.state}`,
    },
  ]
  // job requirement data for component map
  const JOB_REQUIREMENT = [
    {
      id: 1,
      title: 'Qualifications',
      description: jobAdsData?.qualification,
    },
    {
      id: 2,
      title: 'Grade',
      description:
        jobAdsData?.grade_requirement === 0
          ? 'No minimum grade'
          : jobAdsData?.grade_requirement === null
          ? '-'
          : `>${jobAdsData?.grade_requirement} GPA`,
    },
    {
      id: 3,
      title: 'Courses',
      description:
        jobAdsData?.course === null || jobAdsData?.course === '' ? '-' : jobAdsData?.course,
    },
    {
      id: 4,
      title: 'Work Experience',
      description:
        jobAdsData?.fresh_grade_placement === 1
          ? 'Fresh grads are welcome'
          : `${jobAdsData?.year_of_experience} year of experiences`,
    },
    {
      id: 5,
      title: 'Skills',
      description: jobAdsData?.skill === null ? '-' : jobAdsData?.skill,
    },
  ]

  // Dialog header component
  const AdHeader = () => {
    return (
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item display={{ xs: 'none', sm: 'block' }}>
          <CardMedia
            component="img"
            sx={{
              height: 52,
              width: 52,
              objectFit: 'cover',
              borderRadius: '50%',
              // border: '3px solid',
              // borderColor: 'alternate.main',
            }}
            alt="Company Image"
            src={companyDetails?.data?.organization_logo || default_company_photo}
            loading="lazy"
          />
        </Grid>
        <Grid item xs={10} sm={7}>
          <Typography
            variant="h4"
            sx={{
              //   color: formData.position_title ? 'dark-text.main' : 'light-gray-text-2.main',
              color: 'dark-text.main',
              textTransform: 'capitalize',
            }}
          >
            {jobAdsData?.position_title}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'light-gray-text.main', textTransform: 'capitalize' }}
          >
            {companyDetails?.data?.organization_name || 'No company name'}
          </Typography>
        </Grid>
        <Grid
          item
          xs={3}
          display={{
            xs: 'none',
            sm: 'flex',
          }}
          justifyContent={{
            xs: 'flex-start',
            sm: 'flex-end',
          }}
        >
          {jobAdsData?.expired_date !== null ? (
            <Typography variant="body2" sx={{ color: 'light-gray-text.main' }}>
              Expires on {formattedDate}
            </Typography>
          ) : null}
        </Grid>
        <Grid item display="flex" justifyContent="flex-end" alignItems="center">
          <CloseIcon onClick={handleClose} sx={{ cursor: 'pointer', color: 'dark-text.main' }} />
        </Grid>
      </Grid>
    )
  }

  if (jobAdsIsLoading) {
    return (
      <Dialog
        // fullWidth
        // maxWidth="md"
        open={open}
        onClose={handleClose}
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: 4,
            minWidth: { xs: '100%', sm: 3 / 5 },
          },
        }}
      >
        <DialogTitle></DialogTitle>
        <DialogContent
          sx={{
            bgcolor: 'default-background.main',
            height: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress color="alternate" />
        </DialogContent>
      </Dialog>
    )
  } else if (jobAdsError !== null) {
    // else if(test){
    return (
      <Dialog
        // fullWidth
        // maxWidth="md"
        open={open}
        onClose={handleClose}
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: 4,
            minWidth: { xs: '100%', sm: 3 / 5 },
          },
        }}
      >
        <DialogTitle></DialogTitle>
        <DialogContent
          sx={{
            bgcolor: 'default-background.main',
            height: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AdxEmptyStateV2 />
        </DialogContent>
      </Dialog>
    )
  } else {
    return (
      <Dialog
        // fullWidth
        // maxWidth="md"
        open={open}
        onClose={handleClose}
        scroll="body"
        PaperProps={{
          sx: { borderRadius: 4, maxWidth: { xs: '100%', sm: 3 / 5 } },
        }}
      >
        <DialogTitle>
          <AdHeader />
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'default-background.main' }}>
          {/* Job Details */}
          <Box mt={2}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Job Details
            </Typography>
            <Grid container gap={1} mt={1}>
              {JOB_DETAILS?.map((e) => (
                <Fragment key={e.id}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1" sx={{ color: 'light-gray-text.main' }}>
                      {e.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {e.description}
                    </Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Box>
          {/* Job Description */}
          <Box mt={2}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Job Description
            </Typography>
            <Box mt={1} whiteSpace="pre-wrap">
              {jobAdsData?.job_description}
            </Box>
          </Box>
          {/* Job Requirement */}
          <Box mt={2}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Job Requirement
            </Typography>
            <Grid container gap={1} mt={1}>
              {JOB_REQUIREMENT?.map((e) => (
                <Fragment key={e.id}>
                  <Grid item xs={12} sm={3}>
                    <Typography
                      variant="body1"
                      sx={{ color: 'light-gray-text.main', textTransform: 'capitalize' }}
                    >
                      {e.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="body1">{e.description}</Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
            <Box mt={1}>
              <Typography variant="body1" sx={{ color: 'light-gray-text.main' }}>
                Other Requirements
              </Typography>
              <Box whiteSpace="pre-wrap">{jobAdsData?.job_requirement}</Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    )
  }
}
export default ViewJobAdsDialog
